import React, { useCallback, useEffect, useMemo } from "react";
import { useFrame } from "./FrameContext";
import { useModel } from "./ModelContext";
import { useMedia } from "./MediaContext";
import {
    useMediaStore,
    useCurrentMedia,
    useCurrentMediaFileInferredDimensions,
    useCurrentMediaFrameInferredDimensions,
    useUserAnnotationsSnapshot,
    useFlattenedVideoSourceId,
    useReviewedFrames
} from "@rapid/store";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { ComputedValues, ComputedValuesContext } from "./ComputedValuesContext";
import { FrameInferenceResult, RapidBoxPromptData } from "@rapid/types/inference";
import { omit, values } from "lodash";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";
import { useFps } from "@rapid/contexts/FpsContext";
import { MIN_INFERENCES_SAMPLE } from "@rapid/constants/inference";
import type { Annotation } from "@rapid/types/annotation";
import { Prediction } from "../utils/api/inference";
import { getExtractedFrameIdsForMediaItem, useMediaUtils } from "@rapid/hooks/useMediaUtils";
import { MediaItemStatus } from "@rapid/types/media";
import { getFrameAnnotations } from "@rapid/utils/resolveAnnotations";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { Flows } from "@rapid/types/flows";

export const ComputedValuesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { selectedAnnotationId } = useFrame();
    const { annotationsForFileAndFrameId, predictionsForFileAndFrameSegment, promptLabels } =
        useModel();
    const { storageUploadDir, mediaItems } = useMedia();

    const media = useMediaStore();
    const currentMedia = useCurrentMedia();
    const reviewedFrames = useReviewedFrames();
    const currentMediaFileInferredDimensions = useCurrentMediaFileInferredDimensions();
    const currentMediaFrameInferredDimensions = useCurrentMediaFrameInferredDimensions();

    const { uploadMediaItem } = useMediaUtils();
    const { fps } = useFps();
    const { flow } = useFlowState();

    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceUrl = currentWorkspace?.url ?? "anonymous";
    const userAnnotationsSnapshot = useUserAnnotationsSnapshot();
    const flattenedVideoSourceId = useFlattenedVideoSourceId();

    // Get all annotations from ALL files
    const allAnnotationsAcrossFiles: Annotation[] = useMemo(
        () =>
            values(annotationsForFileAndFrameId).flatMap((annotationsForFile) =>
                values(annotationsForFile).flat()
            ),
        [annotationsForFileAndFrameId]
    );

    // Avoid eagerly flattening video-scale prediction stores. Consumers that need
    // predictions should read the current frame or scan the keyed store lazily.
    const allPredictionsAcrossFiles: Prediction[] = useMemo(() => [], []);

    // Generate combined training data from ALL uploaded/uploading files
    // TODO: I loosened the requirement to include uploading files (ex: for videos) MJS
    // might need to tighten this up later
    const currentPromptingData = useMemo(() => {
        const promptingData: any[] = [];

        // all files and/or extracted frames that have been uploaded
        const uploadedMediaItems: typeof mediaItems = [];

        mediaItems.forEach((mediaItem) => {
            const extractedFrameIds = getExtractedFrameIdsForMediaItem(mediaItem);

            if (mediaItem.status === MediaItemStatus.COMPLETED) {
                uploadedMediaItems.push(mediaItem);
            } else if (extractedFrameIds.length > 0) {
                extractedFrameIds.forEach((id) => {
                    const frame = media[id];
                    if (frame?.status === MediaItemStatus.COMPLETED) {
                        uploadedMediaItems.push(frame);
                    }
                });
            }
        });

        uploadedMediaItems.forEach((mediaItem) => {
            const { naturalDimensions, serverMediaDimensions } = mediaItem.metadata || {};

            if (!mediaItem.id) {
                return;
            }

            const dimensions = naturalDimensions || serverMediaDimensions;
            if (!dimensions) {
                return;
            }

            const fileAnnotations = annotationsForFileAndFrameId[mediaItem.id] || {};

            // Process annotations from this file and add to combined training data
            Object.entries(fileAnnotations).forEach(([frameId, frameAnnotations]) => {
                // Type guard to ensure frameAnnotations is an array
                if (!Array.isArray(frameAnnotations)) {
                    return;
                }

                const validAnnotations = frameAnnotations.filter((annotation) => !annotation.draft);

                if (validAnnotations.length === 0) {
                    return;
                }

                const trainingEntry = {
                    file_uuid: `${workspaceUrl}/${frameId}`,
                    width: dimensions.width,
                    height: dimensions.height,
                    boxes: validAnnotations.map((annotation: any) => ({
                        x: Math.round(annotation.x),
                        y: Math.round(annotation.y),
                        w: Math.round(annotation.width),
                        h: Math.round(annotation.height),
                        negative: false,
                        cls: annotation.label
                    }))
                };

                promptingData.push(trainingEntry);
            });
        });

        return promptingData;
    }, [media, mediaItems, annotationsForFileAndFrameId, workspaceUrl]);

    const currentInferenceSampledData = useMemo<FrameInferenceResult[]>(() => [], []);

    const classIdByLabel = useMemo(() => {
        return new Map(Array.from(promptLabels.keys()).map((label, index) => [label, index]));
    }, [promptLabels]);

    const getUploadFrameMetadata = useCallback(
        (fileId: string, frameId: string, frameSegment: number) => {
            const fileItem = media[fileId];
            const frameItem = media[frameId] || fileItem;
            const dimensions =
                frameItem?.metadata?.serverMediaDimensions ||
                frameItem?.metadata?.naturalDimensions ||
                frameItem?.metadata?.inferredDimensions ||
                fileItem?.metadata?.serverMediaDimensions ||
                fileItem?.metadata?.naturalDimensions ||
                fileItem?.metadata?.inferredDimensions;

            if (!fileItem || !dimensions) {
                return null;
            }

            return {
                frame_number: frameSegment,
                file_uuid: `${workspaceUrl}/${fileId}`,
                image_width: dimensions.width,
                image_height: dimensions.height,
                file_name: fileItem.name,
                file_type: fileItem.fileType,
                ...(fileItem.isVideo ? { fps } : {})
            };
        },
        [fps, media, workspaceUrl]
    );

    const annotationToInferencePrediction = useCallback(
        (annotation: Annotation): FrameInferenceResult["predictions"][number] => ({
            x: annotation.x,
            y: annotation.y,
            width: annotation.width,
            height: annotation.height,
            confidence: annotation.confidence ?? 1,
            class: annotation.label,
            class_id: classIdByLabel.get(annotation.label) ?? 0,
            detection_id: annotation.id,
            ...(annotation.masks ? { masks: annotation.masks } : {}),
            ...(annotation.points ? { points: annotation.points } : {}),
            ...(annotation.type ? { type: annotation.type } : {})
        }),
        [classIdByLabel]
    );

    const getFirstModelSetupData = useCallback(() => {
        const instantPromptData: RapidBoxPromptData[] = [];

        Object.entries(annotationsForFileAndFrameId).forEach(([fileId, frames]) => {
            Object.entries(frames).forEach(([frameId, annotations]) => {
                const promptAnnotations = annotations.filter(
                    (annotation) =>
                        !annotation.draft &&
                        annotation.confidence == null &&
                        promptLabels.has(annotation.label) &&
                        annotation.width > 0 &&
                        annotation.height > 0
                );

                if (promptAnnotations.length === 0) {
                    return;
                }

                const frameItem = media[frameId];
                const fileItem = media[fileId];
                const dimensions =
                    frameItem?.metadata?.serverMediaDimensions ||
                    frameItem?.metadata?.naturalDimensions ||
                    frameItem?.metadata?.inferredDimensions ||
                    fileItem?.metadata?.serverMediaDimensions ||
                    fileItem?.metadata?.naturalDimensions ||
                    fileItem?.metadata?.inferredDimensions;

                if (!dimensions) {
                    return;
                }

                instantPromptData.push({
                    file_uuid: `${workspaceUrl}/${frameId}`,
                    width: dimensions.width,
                    height: dimensions.height,
                    boxes: promptAnnotations.map((annotation) => ({
                        x: Math.round(annotation.x),
                        y: Math.round(annotation.y),
                        w: Math.round(annotation.width),
                        h: Math.round(annotation.height),
                        negative: false,
                        cls: annotation.label,
                        ...(annotation.masks ? { masks: annotation.masks } : {}),
                        ...(annotation.points ? { points: annotation.points } : {}),
                        ...(annotation.type ? { type: annotation.type } : {})
                    }))
                });
            });
        });

        const framesToUpload = new Map<
            string,
            { fileId: string; frameId: string; frameSegment: number; source: "snapshot" | "live" }
        >();

        Object.entries(userAnnotationsSnapshot).forEach(([fileId, frames]) => {
            Object.entries(frames).forEach(([frameId, annotations]) => {
                const firstAnnotation = annotations?.[0];
                const frameSegment =
                    firstAnnotation?.frameSegment ?? media[frameId]?.metadata?.segment ?? 0;
                framesToUpload.set(`${fileId}:${frameId}:${frameSegment}`, {
                    fileId,
                    frameId,
                    frameSegment,
                    source: "snapshot"
                });
            });
        });

        Object.values(reviewedFrames).forEach((frame) => {
            framesToUpload.set(`${frame.fileId}:${frame.frameId}:${frame.frameSegment}`, {
                ...frame,
                source: "live"
            });
        });

        if (framesToUpload.size === 0) {
            Object.entries(annotationsForFileAndFrameId).forEach(([fileId, frames]) => {
                Object.entries(frames).forEach(([frameId, annotations]) => {
                    const firstAnnotation = annotations?.[0];
                    const frameSegment =
                        firstAnnotation?.frameSegment ?? media[frameId]?.metadata?.segment ?? 0;
                    framesToUpload.set(`${fileId}:${frameId}:${frameSegment}`, {
                        fileId,
                        frameId,
                        frameSegment,
                        source: "live"
                    });
                });
            });
        }

        const groundTruthData = Array.from(framesToUpload.values()).flatMap(
            ({ fileId, frameId, frameSegment, source }) => {
                const metadata = getUploadFrameMetadata(fileId, frameId, frameSegment);
                if (!metadata) return [];

                const annotationSource =
                    source === "snapshot" ? userAnnotationsSnapshot : annotationsForFileAndFrameId;
                const frameAnnotations = getFrameAnnotations(
                    annotationSource,
                    fileId,
                    frameId
                ).filter((annotation) => !annotation.draft);

                return [
                    {
                        ...metadata,
                        predictions: frameAnnotations.map(annotationToInferencePrediction)
                    }
                ];
            }
        );
        const groundTruthFrameKeys = new Set(
            groundTruthData.map((frame) => `${frame.file_uuid}:${frame.frame_number}`)
        );
        const predictionDatasetData: FrameInferenceResult[] = [];
        const predictionReviewData: FrameInferenceResult[] = [];

        Object.entries(predictionsForFileAndFrameSegment).forEach(([fileId, frames]) => {
            Object.entries(frames).forEach(([frameSegmentKey, predictions]) => {
                const frameSegment = Number(frameSegmentKey);
                const fileItem = media[fileId];
                const frameId = fileItem?.segmentToFrameMediaItemIdMap?.[frameSegment] || fileId;
                const metadata = getUploadFrameMetadata(fileId, frameId, frameSegment);
                if (!metadata) return;
                if (groundTruthFrameKeys.has(`${metadata.file_uuid}:${metadata.frame_number}`)) {
                    return;
                }

                const nonDraftPredictions = predictions.filter((prediction) => !prediction.draft);
                const acceptedPredictions = nonDraftPredictions.filter((prediction) => {
                    const threshold =
                        promptLabels.get(prediction.class)?.confidenceThreshold ??
                        DEFAULT_CONFIDENCE_THRESHOLD;
                    return prediction.confidence >= threshold;
                });

                const frameData = {
                    ...metadata,
                    predictions:
                        acceptedPredictions.length > 0
                            ? acceptedPredictions.map((prediction) =>
                                  omit(prediction, [
                                      "thumbnailDataUrl",
                                      "label",
                                      "draft",
                                      "fileId",
                                      "frameId",
                                      "frameSegment"
                                  ])
                              )
                            : nonDraftPredictions.map((prediction) =>
                                  omit(prediction, [
                                      "thumbnailDataUrl",
                                      "label",
                                      "draft",
                                      "fileId",
                                      "frameId",
                                      "frameSegment"
                                  ])
                              )
                } as FrameInferenceResult;

                if (acceptedPredictions.length > 0) {
                    predictionDatasetData.push(frameData);
                } else {
                    predictionReviewData.push(frameData);
                }
            });
        });

        return { instantPromptData, groundTruthData, predictionDatasetData, predictionReviewData };
    }, [
        reviewedFrames,
        userAnnotationsSnapshot,
        annotationsForFileAndFrameId,
        predictionsForFileAndFrameSegment,
        media,
        getUploadFrameMetadata,
        annotationToInferencePrediction,
        promptLabels,
        workspaceUrl
    ]);

    const imagesNeededToDeploy = useMemo(() => {
        const promptedFiles = currentPromptingData.map((promptData) => `${promptData.file_uuid}-0`); // 0 hardcoded here because prompts are always relative to images, and images always have frameSegment/frame_number === 0
        const inferredFiles = currentInferenceSampledData.map(
            (inferenceData) => `${inferenceData.file_uuid}-${inferenceData.frame_number}`
        );

        const allFiles = new Set([...promptedFiles, ...inferredFiles]);

        return MIN_INFERENCES_SAMPLE - allFiles.size;
    }, [currentPromptingData, currentInferenceSampledData]);

    const hasEnoughDataToDeploy = useMemo(() => {
        // Text prompting is always enabled, so we always have enough data
        return true;
    }, []);

    // === memo computations ===
    // When a video is "flattened" (annotations view), currentMedia.file is set to
    // the frame, but annotations are stored under [videoId][frameId]. Use the
    // original video ID for the file-level key when flattened.
    const effectiveFileId = flattenedVideoSourceId || currentMedia?.file.id;

    const allCurrentFrameAnnotations = useMemo(() => {
        const frameId = currentMedia?.frame?.id ?? currentMedia?.file.id;
        if (!frameId || !effectiveFileId) return [];

        return getFrameAnnotations(annotationsForFileAndFrameId, effectiveFileId, frameId);
    }, [
        annotationsForFileAndFrameId,
        effectiveFileId,
        currentMedia?.file.id,
        currentMedia?.frame?.id
    ]);

    const currentFrameAnnotations = useMemo(() => {
        return allCurrentFrameAnnotations.filter((a) => !a.draft);
    }, [allCurrentFrameAnnotations]);

    // User annotations as they were before inference overwrote the annotations store.
    // Falls back to live non-draft annotations when no snapshot exists (i.e. inference hasn't run yet).
    // Also merges in any annotations drawn AFTER the snapshot was taken (user adding boxes
    // in the Review "My Annotations" view). User-created/edit-accepted annotations don't carry
    // a `confidence` field; untouched model predictions do.
    const currentFrameUserAnnotations = useMemo(() => {
        const frameId = currentMedia?.frame?.id ?? currentMedia?.file.id;
        if (!frameId || !effectiveFileId) return [];

        const snapshotFrameAnnotations = userAnnotationsSnapshot[effectiveFileId]?.[frameId];
        if (snapshotFrameAnnotations) {
            const snapshotAnnotations = snapshotFrameAnnotations.map((annotation) => ({
                ...annotation
            }));
            const snapshotIds = new Set(snapshotAnnotations.map((a) => a.id));
            const newlyDrawn = allCurrentFrameAnnotations.filter(
                (a) => !snapshotIds.has(a.id) && !a.confidence && !a.draft
            );
            return [...snapshotAnnotations, ...newlyDrawn];
        }

        if (Object.keys(userAnnotationsSnapshot).length > 0) {
            return allCurrentFrameAnnotations.filter((a) => !a.draft && !a.confidence);
        }

        return allCurrentFrameAnnotations.filter((a) => !a.draft);
    }, [
        effectiveFileId,
        currentMedia?.frame?.id,
        currentMedia?.file.id,
        userAnnotationsSnapshot,
        allCurrentFrameAnnotations
    ]);

    const currentFramePredictions = useMemo(() => {
        // For images, frameSegment is undefined, treat as 0. For videos, it's the segment number.
        const frameSegment = currentMedia?.frameSegment ?? 0;

        const predictions =
            predictionsForFileAndFrameSegment[currentMedia?.file.id]?.[frameSegment] || [];

        // In DEPLOY/SHARE flows, workflow inference returns predictions with workflow classes
        // Don't filter by promptLabels since we're not building a model
        if (flow === Flows.DEPLOY || flow === Flows.SHARE) {
            return predictions;
        }

        // In BUILD flow, filter by promptLabels
        const filtered = predictions.filter((p) => {
            const promptLabel = promptLabels.get(p.class);
            if (!promptLabel) return false;
            return true;
        });

        return filtered;
    }, [
        predictionsForFileAndFrameSegment,
        currentMedia?.file.id,
        currentMedia?.frameSegment,
        promptLabels,
        flow
    ]);

    const manageVideoCurrentFrameStorage = useCallback(() => {
        if (!currentMedia?.file.isVideo) return;
        if (!currentMedia?.frame) return;

        const isUploaded = [MediaItemStatus.COMPLETED, MediaItemStatus.UPLOADING].includes(
            currentMedia?.frame?.status
        );

        if (currentMedia?.frame && currentFrameAnnotations.length > 0 && !isUploaded) {
            // upload frame to storage
            uploadMediaItem(currentMedia.frame, { storageUploadDir });
        }
    }, [
        currentMedia?.frame,
        currentMedia?.file.isVideo,
        storageUploadDir,
        currentFrameAnnotations,
        uploadMediaItem
    ]);

    useEffect(() => {
        if (currentMedia?.file.isVideo && currentMedia?.frame) {
            manageVideoCurrentFrameStorage();
        }
    }, [currentMedia?.file.isVideo, currentMedia?.frame, manageVideoCurrentFrameStorage]);

    const inferredDimensions = useMemo(() => {
        if (currentMedia?.file.isVideo) {
            return currentMediaFrameInferredDimensions || currentMediaFileInferredDimensions;
        }

        return currentMediaFileInferredDimensions;
    }, [
        currentMedia?.file.isVideo,
        currentMediaFrameInferredDimensions,
        currentMediaFileInferredDimensions
    ]);

    const selectedObject = useMemo(() => {
        if (!selectedAnnotationId) return null;
        const anno = currentFrameAnnotations.find((a) => a.id === selectedAnnotationId);
        return anno?.label || null;
    }, [selectedAnnotationId, currentFrameAnnotations]);

    const value = useMemo<ComputedValues>(
        () => ({
            allCurrentFrameAnnotations,
            currentFrameAnnotations,
            currentFrameUserAnnotations,
            currentFramePredictions,
            inferredDimensions,
            currentPromptingData,
            currentInferenceSampledData,
            getFirstModelSetupData,
            selectedObject,
            allAnnotationsAcrossFiles,
            allPredictionsAcrossFiles,
            imagesNeededToDeploy,
            hasEnoughDataToDeploy
        }),
        [
            allCurrentFrameAnnotations,
            currentFrameAnnotations,
            currentFrameUserAnnotations,
            currentFramePredictions,
            inferredDimensions,
            currentPromptingData,
            currentInferenceSampledData,
            getFirstModelSetupData,
            selectedObject,
            allAnnotationsAcrossFiles,
            allPredictionsAcrossFiles,
            imagesNeededToDeploy,
            hasEnoughDataToDeploy
        ]
    );

    return (
        <ComputedValuesContext.Provider value={value}>{children}</ComputedValuesContext.Provider>
    );
};
